import type { HubRecordsDto } from "@bafe/api-client";

export const TASK_BOARD_STATUSES = ["new", "pending", "reviewed", "completed"] as const;
export type TaskBoardStatus = (typeof TASK_BOARD_STATUSES)[number];

export type HubTask = HubRecordsDto["tasks"][number];
export type TaskColumnIds = Record<TaskBoardStatus, string[]>;

export function taskStatusOf(status: string): TaskBoardStatus {
	return TASK_BOARD_STATUSES.includes(status as TaskBoardStatus) ? status as TaskBoardStatus : "new";
}

export function boardSortKey(task: HubTask): number {
	const position = task.boardPosition ?? 0;
	if (position > 0) return position;
	const id = Number(task.id);
	return Number.isFinite(id) ? id * 1024 : 0;
}

export function columnTasks(tasks: HubTask[], status: TaskBoardStatus): HubTask[] {
	return tasks
		.filter((row) => taskStatusOf(row.status) === status)
		.sort((left, right) => boardSortKey(left) - boardSortKey(right) || Number(left.id) - Number(right.id));
}

export function positionBetween(before?: number, after?: number): number {
	if (after == null) return (before ?? 0) + 1024;
	if (before == null || before <= 0) return after - 512;
	return (before + after) / 2;
}

export function columnIdsFromTasks(tasks: HubTask[]): TaskColumnIds {
	return Object.fromEntries(
		TASK_BOARD_STATUSES.map((status) => [
			status,
			columnTasks(tasks, status).map((row) => row.id).filter(Boolean),
		]),
	) as TaskColumnIds;
}

export function columnId(status: TaskBoardStatus): string {
	return `task-col:${status}`;
}

export function columnTopId(status: TaskBoardStatus): string {
	return `task-col-top:${status}`;
}

export function parseColumnId(id: string): TaskBoardStatus | null {
	if (!id.startsWith("task-col:") || id.startsWith("task-col-top:")) return null;
	return taskStatusOf(id.slice("task-col:".length));
}

export function parseColumnTopId(id: string): TaskBoardStatus | null {
	return id.startsWith("task-col-top:") ? taskStatusOf(id.slice("task-col-top:".length)) : null;
}

export function findTaskColumn(columns: TaskColumnIds, taskId: string): TaskBoardStatus | null {
	for (const status of TASK_BOARD_STATUSES) {
		if (columns[status].includes(taskId)) return status;
	}
	return null;
}

/** Insert index in a column list (0..length) from pointer Y vs hovered card. */
export function sortableInsertIndex(
	pointerY: number,
	overTop: number,
	overHeight: number,
	overIndex: number,
): number {
	if (overHeight <= 0) return overIndex;
	const mid = overTop + overHeight / 2;
	return pointerY < mid ? overIndex : overIndex + 1;
}

/** Walk visible cards in column DOM order to match pointer Y (Trello-style). */
function cssEscape(value: string): string {
	return globalThis.CSS?.escape?.(value) ?? value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

function statusTabsNavElement(): HTMLElement | null {
	if (typeof document === "undefined") return null;
	const nav = document.querySelector(".hub .task-status-tabs");
	return nav instanceof HTMLElement ? nav : null;
}

/** True when the pointer is over the mobile status tab strip (not the column list). */
export function pointerInStatusTabsBar(x: number, y: number): boolean {
	if (!Number.isFinite(x) || !Number.isFinite(y)) return false;
	const nav = statusTabsNavElement();
	if (!nav) return false;
	const rect = nav.getBoundingClientRect();
	return (
		x >= rect.left - 8
		&& x <= rect.right + 8
		&& y >= rect.top - 6
		&& y <= rect.bottom + 6
	);
}

export function statusTabAtPointer(x: number, y: number): TaskBoardStatus | null {
	if (typeof document === "undefined" || !Number.isFinite(x) || !Number.isFinite(y)) return null;
	const hitStack = document.elementsFromPoint?.(x, y);
	const fallback = document.elementFromPoint?.(x, y);
	const stack = hitStack ?? (fallback ? [fallback] : []);
	for (const node of stack) {
		if (!(node instanceof HTMLElement)) continue;
		if (node.closest(".task-drag-overlay")) continue;
		const tab = node.closest("[data-task-status-tab]");
		if (tab instanceof HTMLElement) {
			const status = tab.dataset.taskStatusTab ?? "";
			if (TASK_BOARD_STATUSES.includes(status as TaskBoardStatus)) {
				return status as TaskBoardStatus;
			}
		}
	}
	return null;
}

/** Tab column only when the finger is on the tab bar — avoids false Pending while reordering in New. */
export function statusTabAtPointerFromBar(x: number, y: number): TaskBoardStatus | null {
	if (!pointerInStatusTabsBar(x, y)) return null;
	return statusTabAtPointer(x, y);
}

export function columnAtPointer(x: number, y: number): TaskBoardStatus | null {
	const tab = statusTabAtPointer(x, y);
	if (tab) return tab;
	if (typeof document === "undefined" || !Number.isFinite(x) || !Number.isFinite(y)) return null;
	const hitStack = document.elementsFromPoint?.(x, y);
	const fallback = document.elementFromPoint?.(x, y);
	const stack = hitStack ?? (fallback ? [fallback] : []);
	for (const node of stack) {
		if (!(node instanceof HTMLElement)) continue;
		if (node.closest(".task-drag-overlay")) continue;
		const col = node.closest("[data-task-col]");
		if (!(col instanceof HTMLElement)) continue;
		const bounds = col.getBoundingClientRect();
		if (x < bounds.left - 12 || x > bounds.right + 12) continue;
		if (y < bounds.top - 24 || y > bounds.bottom + 12) continue;
		const status = col.dataset.taskCol ?? "";
		if (TASK_BOARD_STATUSES.includes(status as TaskBoardStatus)) {
			return status as TaskBoardStatus;
		}
	}
	return null;
}

/** Insert index from rendered card order (matches what the user sees while dragging). */
export function insertIndexFromColumnDom(
	columnRoot: ParentNode | null,
	activeTaskId: string,
	pointerY: number,
): number {
	if (!columnRoot) return 0;
	const cards = columnRoot.querySelectorAll("[data-task-id]");
	let index = 0;
	for (const node of cards) {
		if (!(node instanceof HTMLElement)) continue;
		const id = node.dataset.taskId ?? "";
		if (!id || id === activeTaskId) continue;
		const rect = node.getBoundingClientRect();
		if (rect.height < 4) continue;
		const mid = rect.top + rect.height / 2;
		if (pointerY < mid) return index;
		index += 1;
	}
	return index;
}

const COLUMN_TOP_DROP_BAND_PX = 40;

/** Layout slots (immune to sortable transform visuals) for stable insert index while dragging. */
export function insertIndexFromColumnLayout(
	columnList: ParentNode | null,
	activeTaskId: string,
	pointerY: number,
): number {
	if (!(columnList instanceof HTMLElement)) {
		return insertIndexFromColumnDom(columnList, activeTaskId, pointerY);
	}
	const scrollBody = columnList.closest(".task-col-body");
	const scrollTop = scrollBody instanceof HTMLElement ? scrollBody.scrollTop : 0;
	const listTop = columnList.getBoundingClientRect().top;
	const relativeY = pointerY - listTop + scrollTop;
	const slots = columnList.querySelectorAll(".task-sortable-slot");
	let index = 0;
	for (const slot of slots) {
		if (!(slot instanceof HTMLElement)) continue;
		if (slot.classList.contains("is-dragging")) continue;
		const card = slot.querySelector("[data-task-id]");
		const id = card instanceof HTMLElement ? card.dataset.taskId ?? "" : "";
		if (id && id === activeTaskId) continue;
		const slotHeight = slot.offsetHeight;
		if (slotHeight < 4) continue;
		const mid = slot.offsetTop + slotHeight / 2;
		if (relativeY < mid) return index;
		index += 1;
	}
	return index;
}

/** Scroll a long column to the top when the pointer is in the header drop band. */
export function syncColumnScrollForTopDrop(columnStatus: TaskBoardStatus, pointerY: number): void {
	if (typeof document === "undefined") return;
	const col = document.querySelector(`[data-task-col="${columnStatus}"]`);
	const scrollBody = col?.querySelector(".task-col-body");
	if (!(scrollBody instanceof HTMLElement)) return;
	const bodyRect = scrollBody.getBoundingClientRect();
	if (pointerY <= bodyRect.top + COLUMN_TOP_DROP_BAND_PX) {
		scrollBody.scrollTop = 0;
	}
}

function insertIndexInColumn(
	columnRoot: ParentNode | null,
	columnStatus: TaskBoardStatus,
	activeTaskId: string,
	pointerY: number,
	orderedIds: readonly string[],
): number {
	const withoutActive = orderedIds.filter((id) => id !== activeTaskId);
	if (!(columnRoot instanceof HTMLElement) || typeof document === "undefined") {
		return Math.min(insertIndexFromColumnDom(columnRoot, activeTaskId, pointerY), withoutActive.length);
	}

	const col = columnRoot.closest(`[data-task-col="${columnStatus}"]`);
	const scrollBody = col?.querySelector(".task-col-body");
	const topDrop = col?.querySelector(".task-col-top-drop");

	if (topDrop instanceof HTMLElement) {
		const topRect = topDrop.getBoundingClientRect();
		if (pointerY <= topRect.bottom + 4) return 0;
	}
	if (scrollBody instanceof HTMLElement) {
		const bodyRect = scrollBody.getBoundingClientRect();
		if (pointerY <= bodyRect.top + COLUMN_TOP_DROP_BAND_PX) return 0;
	}

	for (let index = 0; index < withoutActive.length; index += 1) {
		const id = withoutActive[index]!;
		const card = columnRoot.querySelector(`[data-task-id="${cssEscape(id)}"]`);
		const slot = card?.closest(".task-sortable-slot");
		if (!(slot instanceof HTMLElement) || slot.classList.contains("is-dragging")) continue;
		const rect = slot.getBoundingClientRect();
		if (rect.height < 4) continue;
		if (scrollBody instanceof HTMLElement) {
			const bodyRect = scrollBody.getBoundingClientRect();
			if (rect.bottom < bodyRect.top - 8) continue;
			if (rect.top > bodyRect.bottom + 8) break;
		}
		const mid = rect.top + rect.height / 2;
		if (pointerY < mid) return index;
	}

	return withoutActive.length;
}

export function resolveDropTarget(
	columns: TaskColumnIds,
	activeTaskId: string,
	overRaw: string,
	pointer: { x: number; y: number },
): { status: TaskBoardStatus; insertIndex: number } | null {
	const topSlot = parseColumnTopId(overRaw);
	const emptyColumn = parseColumnId(overRaw);
	const overColumn = findTaskColumn(columns, overRaw);
	const activeColumn = findTaskColumn(columns, activeTaskId);

	if (topSlot) {
		return { status: topSlot, insertIndex: 0 };
	}

	const tabStatus = statusTabAtPointerFromBar(pointer.x, pointer.y);
	if (tabStatus) {
		const ids = columns[tabStatus].filter((id) => id !== activeTaskId);
		return { status: tabStatus, insertIndex: ids.length };
	}

	if (activeTaskId === overRaw && activeColumn) {
		const columnRoot = typeof document !== "undefined"
			? document.querySelector(`[data-task-col="${activeColumn}"] .task-col-list`)
			: null;
		const ids = columns[activeColumn].filter((id) => id !== activeTaskId);
		syncColumnScrollForTopDrop(activeColumn, pointer.y);
		const col = columnRoot?.closest(`[data-task-col="${activeColumn}"]`);
		const scrollBody = col?.querySelector(".task-col-body");
		const topDrop = col?.querySelector(".task-col-top-drop");
		const inTopBand = (scrollBody instanceof HTMLElement
			&& pointer.y <= scrollBody.getBoundingClientRect().top + COLUMN_TOP_DROP_BAND_PX)
			|| (topDrop instanceof HTMLElement
				&& pointer.y <= topDrop.getBoundingClientRect().bottom + 4);
		const insertIndex = inTopBand
			? 0
			: Math.max(
				0,
				Math.min(
					insertIndexInColumn(columnRoot, activeColumn, activeTaskId, pointer.y, columns[activeColumn]),
					ids.length,
				),
			);
		return { status: activeColumn, insertIndex };
	}

	const status = overColumn
		?? emptyColumn
		?? columnAtPointer(pointer.x, pointer.y);
	if (!status) return null;

	if (emptyColumn === status && columns[status].length === 0) {
		return { status, insertIndex: 0 };
	}

	const columnRoot = typeof document !== "undefined"
		? document.querySelector(`[data-task-col="${status}"] .task-col-list`)
		: null;
	const ids = columns[status].filter((id) => id !== activeTaskId);

	syncColumnScrollForTopDrop(status, pointer.y);
	const insertIndex = Math.max(
		0,
		Math.min(
			insertIndexInColumn(columnRoot, status, activeTaskId, pointer.y, columns[status]),
			ids.length,
		),
	);
	return { status, insertIndex };
}

export function columnOrderKey(columns: TaskColumnIds): string {
	return TASK_BOARD_STATUSES.map((status) => columns[status].join(",")).join("|");
}

/**
 * Hold a one-slot preview shift until the pointer leaves the midpoint dead zone.
 * A stable pointer on the boundary otherwise flips the index every measure pass
 * and nests setState until React throws error 185.
 */
export function previewIndexHeld(
	previous: { status: TaskBoardStatus; insertIndex: number } | null,
	next: { status: TaskBoardStatus; insertIndex: number },
	pointerTravelY: number,
	holdPx = 12,
): boolean {
	if (!previous || previous.status !== next.status) return false;
	const delta = next.insertIndex - previous.insertIndex;
	if (Math.abs(delta) !== 1) return false;
	return delta > 0 ? pointerTravelY < holdPx : pointerTravelY > -holdPx;
}

/** Final drop on release — never treats “over self” as staying at the old slot. */
export function resolveFinalDropTarget(
	columns: TaskColumnIds,
	activeTaskId: string,
	pointer: { x: number; y: number },
	overRawHint: string | null,
): { status: TaskBoardStatus; insertIndex: number } | null {
	const topSlot = overRawHint ? parseColumnTopId(overRawHint) : null;
	if (topSlot) {
		return { status: topSlot, insertIndex: 0 };
	}

	const tabStatus = statusTabAtPointerFromBar(pointer.x, pointer.y);
	if (tabStatus) {
		const ids = columns[tabStatus].filter((id) => id !== activeTaskId);
		return { status: tabStatus, insertIndex: ids.length };
	}

	const status = (overRawHint ? findTaskColumn(columns, overRawHint) : null)
		?? (overRawHint ? parseColumnId(overRawHint) : null)
		?? columnAtPointer(pointer.x, pointer.y)
		?? findTaskColumn(columns, activeTaskId);
	if (!status) return null;

	const emptyColumn = overRawHint ? parseColumnId(overRawHint) : null;
	if (emptyColumn === status && columns[status].length === 0) {
		return { status, insertIndex: 0 };
	}

	const columnRoot = typeof document !== "undefined"
		? document.querySelector(`[data-task-col="${status}"] .task-col-list`)
		: null;
	const ids = columns[status].filter((id) => id !== activeTaskId);

	syncColumnScrollForTopDrop(status, pointer.y);
	const insertIndex = Math.max(
		0,
		Math.min(
			insertIndexInColumn(columnRoot, status, activeTaskId, pointer.y, columns[status]),
			ids.length,
		),
	);
	return { status, insertIndex };
}

export function applyColumnMove(
	columns: TaskColumnIds,
	activeTaskId: string,
	targetStatus: TaskBoardStatus,
	insertIndex: number,
): TaskColumnIds | null {
	const activeContainer = findTaskColumn(columns, activeTaskId);
	if (!activeContainer) return null;

	const list = columns[targetStatus];
	const fromIndex = list.indexOf(activeTaskId);
	const withoutActive = fromIndex >= 0 ? list.filter((id) => id !== activeTaskId) : list;
	const clamped = Math.max(0, Math.min(insertIndex, withoutActive.length));
	const target = [...withoutActive];
	target.splice(clamped, 0, activeTaskId);

	if (activeContainer === targetStatus && list.join("|") === target.join("|")) return null;

	const next: TaskColumnIds = {
		new: [...columns.new],
		pending: [...columns.pending],
		reviewed: [...columns.reviewed],
		completed: [...columns.completed],
	};
	if (activeContainer !== targetStatus) {
		next[activeContainer] = next[activeContainer].filter((id) => id !== activeTaskId);
	}
	next[targetStatus] = target;
	return next;
}

export function insertIndexForColumnPointer(
	columnRoot: ParentNode | null,
	taskIds: readonly string[],
	pointerY: number,
): number {
	if (!columnRoot || taskIds.length === 0) return 0;
	for (let index = 0; index < taskIds.length; index++) {
		const id = taskIds[index]!;
		const el = columnRoot.querySelector(`[data-task-id="${cssEscape(id)}"]`);
		if (!(el instanceof HTMLElement)) continue;
		const rect = el.getBoundingClientRect();
		if (rect.height <= 0) continue;
		const mid = rect.top + rect.height / 2;
		if (pointerY < mid) return index;
	}
	return taskIds.length;
}

export function boardMoveFromOrder(
	taskId: string,
	status: TaskBoardStatus,
	orderedIds: string[],
	tasks: HubTask[],
): { status: TaskBoardStatus; boardPosition: number } | null {
	const index = orderedIds.indexOf(taskId);
	if (index < 0) return null;
	const byId = new Map(tasks.map((row) => [row.id, row]));
	const task = byId.get(taskId);
	if (!task) return null;
	const beforeTask = index > 0 ? byId.get(orderedIds[index - 1]!) : undefined;
	const afterTask = index < orderedIds.length - 1 ? byId.get(orderedIds[index + 1]!) : undefined;
	let boardPosition = positionBetween(
		beforeTask ? boardSortKey(beforeTask) : undefined,
		afterTask ? boardSortKey(afterTask) : undefined,
	);
	if (!Number.isFinite(boardPosition)) {
		boardPosition = boardSortKey(task) || 1024;
	}
	const fromStatus = taskStatusOf(task.status);
	const serverOrder = columnIdsFromTasks(tasks);
	const orderChanged = serverOrder[status].join("|") !== orderedIds.join("|")
		|| fromStatus !== status;
	if (!orderChanged) return null;
	return { status, boardPosition };
}
